export function mapCondicoesPagamentoFromApi(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((c) => ({
      idCondicaoPagamento: Number(c.IdCondicaoPagamento ?? c.idCondicaoPagamento),
      politica: (c.Politica ?? c.politica ?? "").trim(),
      percentualDesconto: c.PercentualDesconto ?? c.percentualDesconto ?? null,
      // Encargo financeiro da condição (wmw.ws_cond_pagto.pcecf) — somado ao preço, ver netUnitPrice.
      // Sempre do catálogo, sem override por vitrine (diferente de percentualDesconto).
      percentualEncargo: c.PercentualEncargo ?? c.percentualEncargo ?? null,
      prazoMedio: c.PrazoMedio ?? c.prazoMedio ?? null,
      label: formatCondicaoPagamentoLabel(c),
    }))
    .filter((c) => c.idCondicaoPagamento > 0);
}

export function formatCondicaoPagamentoLabel(condition) {
  if (!condition) return "";
  const politica =
    (condition.Politica ?? condition.politica ?? "").trim() ||
    (condition.idCondicaoPagamento != null
      ? `Condição ${condition.idCondicaoPagamento}`
      : condition.IdCondicaoPagamento != null
        ? `Condição ${condition.IdCondicaoPagamento}`
        : "");
  const parts = [politica];
  const pct = condition.PercentualDesconto ?? condition.percentualDesconto;
  if (pct != null && pct !== "") {
    parts.push(`${pct}% desc.`);
  }
  const prazo = condition.PrazoMedio ?? condition.prazoMedio;
  if (prazo != null && prazo !== "") {
    parts.push(`${prazo} dias`);
  }
  return parts.join(" · ");
}

export function getDefaultCondicaoPagamentoId(condicoes, fallbackId) {
  if (!Array.isArray(condicoes) || condicoes.length === 0) {
    const fb = Number(fallbackId);
    return fb > 0 ? fb : null;
  }
  if (condicoes.length === 1) return condicoes[0].idCondicaoPagamento;
  const fb = Number(fallbackId);
  if (fb > 0 && condicoes.some((c) => c.idCondicaoPagamento === fb)) {
    return fb;
  }
  return condicoes[0].idCondicaoPagamento;
}

export function findCondicaoPagamentoById(condicoes, id) {
  if (id == null || !Array.isArray(condicoes)) return null;
  const n = Number(id);
  return condicoes.find((c) => c.idCondicaoPagamento === n) || null;
}

export function getDiscountPercentFromCondicao(condicao, fallback = 0) {
  if (!condicao) return Number(fallback) || 0;
  const pct = condicao.percentualDesconto ?? condicao.PercentualDesconto;
  if (pct == null || pct === "") return Number(fallback) || 0;
  const n = Number(pct);
  return Number.isNaN(n) ? Number(fallback) || 0 : n;
}

/**
 * Condições de pagamento da vitrine DO PRODUTO. Uma família reúne produtos de várias vitrines da mesma
 * marca, mas guarda só as condições da primeira vitrine que a criou: usar fam.condicoesPagamento para um
 * produto de outra vitrine manda ao servidor uma condição que essa vitrine não tem.
 */
export function condicoesDoProduto(prod, fam) {
  return prod?.condicoesPagamento ?? fam?.condicoesPagamento ?? [];
}

export function condicaoPadraoDoProduto(prod, fam) {
  return getDefaultCondicaoPagamentoId(
    condicoesDoProduto(prod, fam),
    prod?.idCondicaoPagamentoPadrao ?? fam?.idCondicaoPagamentoPadrao,
  );
}

/** Desconto efetivo do SKU conforme condição de pagamento selecionada. */
export function resolveLineDiscount(prod, fam, idCondicaoPagamento) {
  const fallback = Number(prod?.discount) || 0;
  const condicoes = condicoesDoProduto(prod, fam);
  const cond = findCondicaoPagamentoById(condicoes, idCondicaoPagamento);
  if (cond) return getDiscountPercentFromCondicao(cond, fallback);
  return fallback;
}

/** Encargo financeiro (wmw.ws_cond_pagto.pcecf) da condição de pagamento selecionada — sempre do
 * catálogo, sem fallback por produto (diferente do desconto, não é configurável por item/vitrine). */
export function resolveLineEncargo(fam, idCondicaoPagamento, prod) {
  const condicoes = condicoesDoProduto(prod, fam);
  const cond = findCondicaoPagamentoById(condicoes, idCondicaoPagamento);
  const pct = cond?.percentualEncargo;
  if (pct == null || pct === "") return 0;
  const n = Number(pct);
  return Number.isNaN(n) ? 0 : n;
}

// Encargo é somado ANTES do desconto: (preço * (1 + encargo%)) * (1 - desconto%). Ex.: produto de
// R$100 com 10% de encargo e 10% de desconto = (100 * 1,10) * 0,90 = R$99.
export function netUnitPrice(prod, discountPercent, encargoPercent = 0) {
  const price = Number(prod?.price) || 0;
  const e = Number(encargoPercent) || 0;
  const d = Number(discountPercent) || 0;
  return price * (1 + e / 100) * (1 - d / 100);
}

// Preço "de" (referência mostrada riscada antes do desconto) — sempre com o encargo já embutido.
// Nunca mostrar o preço base cru como se já fosse o valor final sem desconto: o cliente tem que
// ver "de R$110 por R$99", nunca "de R$100 por R$99" (que esconderia o encargo aplicado por trás
// do desconto). Não é o preço base puro nem um valor separado de encargo — é sempre um único
// número já com o encargo somado.
export function grossUnitPrice(prod, encargoPercent = 0) {
  const price = Number(prod?.price) || 0;
  const e = Number(encargoPercent) || 0;
  return price * (1 + e / 100);
}
